import { useEffect, useRef } from "react";
import { ArrowRight, Sparkles, Users, Calendar } from "lucide-react";
import { trackViewContent } from "@/lib/tracking";

/**
 * Planning Experiences / Packages section — public landing page.
 *
 * Curiosity-led: each card shows only a short summary (subtitle, name, tagline,
 * and 3 headline facts at most). No package breakdowns, no comparison links,
 * no brochure downloads — every CTA funnels straight to the contact form.
 *
 * Note: package CMS data (includes / excludes / brochure_url / comparison_pdf_url)
 * is intentionally NOT shown on the public site but is preserved untouched in the
 * database — admins can still edit it from the CMS for future use.
 */
export default function Planning({ data }) {
  const label = data?.label || "Planning experiences";
  const headline = data?.headline || "Wedding planning, built around you.";
  const subheadline = data?.subheadline || "";
  const items = data?.items || [];
  const ctaLabel = "Request Our Complete Package";
  const ctaHref = "#contact";

  // Fire Meta `ViewContent` + GA4 `view_item` exactly once when the section enters the viewport.
  const sectionRef = useRef(null);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            trackViewContent(
              {
                content_name: "planning_experiences",
                content_category: "packages",
                content_ids: items.map((t) => t.slug || t.name).filter(Boolean),
                num_items: items.length,
              },
              "planning_section",
            );
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.35 }, // fire when ~35% of the section is in view
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [items]);

  return (
    <section id="planning" ref={sectionRef} data-testid="planning-section" className="bg-ivory py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <div className="reveal flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-gold" />
            <span className="eyebrow">{label}</span>
            <span className="h-px w-10 bg-gold" />
          </div>
          <h2
            data-testid="planning-headline"
            className="reveal font-display text-[2.25rem] leading-[1.05] sm:text-5xl md:text-6xl tracking-tight text-charcoal"
          >
            {headline}
          </h2>
          {subheadline ? (
            <p className="reveal mt-5 text-charcoal/70 font-light leading-relaxed">{subheadline}</p>
          ) : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {items.map((tier, i) => (
            <article
              key={tier.slug || tier.name}
              data-testid={`planning-tier-${i}`}
              className={`reveal relative bg-white border ${tier.featured ? "border-gold shadow-card" : "border-beige"} p-7 md:p-9 flex flex-col`}
            >
              {tier.featured ? (
                <span className="absolute -top-3 left-7 bg-gold text-charcoal px-3 py-1 uppercase tracking-[0.22em] text-[10px] font-medium">
                  Most chosen
                </span>
              ) : null}
              {tier.subtitle ? (
                <div className="uppercase tracking-[0.25em] text-[10px] text-teal-600 mb-2">{tier.subtitle}</div>
              ) : null}
              <h3 className="font-display text-2xl md:text-3xl text-charcoal leading-tight">{tier.name}</h3>
              <p className="mt-3 text-charcoal/65 italic font-light leading-relaxed">{tier.tagline}</p>

              {/* Headline facts only — curiosity, not a breakdown. */}
              {(tier.starts_from || tier.events_covered || tier.team) ? (
                <ul className="mt-6 space-y-2 text-[12.5px] text-charcoal/70 font-light border-y border-beige py-4">
                  {tier.starts_from ? (
                    <li className="flex items-start gap-2">
                      <Calendar className="h-3.5 w-3.5 mt-0.5 text-gold shrink-0" strokeWidth={1.5} />
                      <span><span className="uppercase tracking-wider text-[10px] text-charcoal/50">Starts: </span>{tier.starts_from}</span>
                    </li>
                  ) : null}
                  {tier.events_covered ? (
                    <li className="flex items-start gap-2">
                      <Sparkles className="h-3.5 w-3.5 mt-0.5 text-gold shrink-0" strokeWidth={1.5} />
                      <span><span className="uppercase tracking-wider text-[10px] text-charcoal/50">Events: </span>{tier.events_covered}</span>
                    </li>
                  ) : null}
                  {tier.team ? (
                    <li className="flex items-start gap-2">
                      <Users className="h-3.5 w-3.5 mt-0.5 text-gold shrink-0" strokeWidth={1.5} />
                      <span><span className="uppercase tracking-wider text-[10px] text-charcoal/50">Team: </span>{tier.team}</span>
                    </li>
                  ) : null}
                </ul>
              ) : null}

              {tier.ideal_for ? (
                <p className="mt-5 text-sm text-charcoal/75 font-light leading-relaxed">
                  <span className="uppercase tracking-wider text-[10px] text-charcoal/50 block mb-1">Ideal for</span>
                  {tier.ideal_for}
                </p>
              ) : null}

              <div className="mt-auto pt-8">
                <a
                  href={ctaHref}
                  data-testid={`planning-cta-${i}`}
                  className={`group inline-flex items-center justify-between gap-2 h-12 w-full px-5 text-[11px] uppercase tracking-[0.22em] font-medium border transition-colors ${
                    tier.featured
                      ? "bg-charcoal text-ivory border-charcoal hover:bg-gold hover:text-charcoal hover:border-gold"
                      : "border-charcoal/25 text-charcoal hover:bg-charcoal hover:text-ivory"
                  }`}
                >
                  {ctaLabel}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
